import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

import { Stack } from "@mui/material";
import {
  convertCountryNameToCountryCode,
  CountryCode,
  CountryNames,
  FormType,
  getRegionDisplayName,
  isRaffleBuyerStateBanEnforced,
  isRaffleStateBanned,
  OrganizationCountry,
  TicketingFormCategory,
} from "@simplyk/common";
import { AddressElement, useElements } from "@stripe/react-stripe-js";
import { Control, FieldError, useController } from "react-hook-form";

import { useFormV2Context } from "@/canvas/features/FormV2/contexts/FormV2Context";
import { AmplitudeEvents } from "@/common/constants/amplitude";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";
import { useCountryRegion } from "@/common/hooks/useCountryRegion";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TicketingPaymentInput } from "@/common/types/ticketing";
import { FormButton } from "@/forms-design-system/FormButton";
import { FormCheckbox } from "@/forms-design-system/FormCheckbox";
import { FormDialog } from "@/forms-design-system/FormDialog";
import { FormTypography } from "@/forms-design-system/FormTypography/FormTypography";
import { Warning } from "@/icons/outlined";

export interface RaffleEligibilityContentProps {
  organizationName: string;
  regionName: string;
  open: boolean;
  onClose: () => void;
}

export const RaffleAdultConfirmation = ({
  control,
  error,
}: {
  control: Control<TicketingPaymentInput>;
  error?: FieldError;
}) => {
  const { t } = useTranslate();
  const checkboxId = useId();

  return (
    <FormCheckbox
      control={control}
      name="isAdultConfirmed"
      id={checkboxId}
      label={t("ticketing", "raffle.adultConfirmation")}
      rules={[
        {
          validate: (value: unknown) => Boolean(value) || t("ticketing", "raffle.adultRequired"),
        },
      ]}
      error={Boolean(error)}
      helperText={error?.message}
      data-test="raffle-adult-confirmation"
    />
  );
};

export const RaffleEligibilityDialogBody = ({
  organizationName,
  regionName,
  onClose,
}: Omit<RaffleEligibilityContentProps, "open">) => {
  const { t } = useTranslate();

  return (
    <Stack gap={3}>
      <Stack gap={1}>
        <FormTypography variant="h5" vibe="intense">
          {t("ticketing", "raffle.blockerTitle", { state: regionName })}
        </FormTypography>
        <FormTypography variant="body1" vibe="moderate">
          {t("ticketing", "raffle.blockerBody", { state: regionName, organizationName })}
        </FormTypography>
      </Stack>
      <FormButton type="button" onClick={onClose} sx={{ alignSelf: "flex-end" }}>
        {t("common", "close")}
      </FormButton>
    </Stack>
  );
};

export const RaffleEligibilityContent = ({
  organizationName,
  regionName,
  open,
  onClose,
}: RaffleEligibilityContentProps) => {
  const { t } = useTranslate();
  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={t("ticketing", "raffle.blockerTitle", { state: regionName })}
      icon={<Warning />}
      size="xs"
      actions={[
        <FormButton key="close" onClick={onClose}>
          {t("common", "close")}
        </FormButton>,
      ]}
    >
      <FormTypography variant="body2" vibe="moderate" data-test="raffle-eligibility-dialog">
        {t("ticketing", "raffle.blockerBody", { state: regionName, organizationName })}
      </FormTypography>
    </FormDialog>
  );
};

const hasRaffleEntries = (ticketsPurchased: TicketingPaymentInput["ticketsPurchased"]) =>
  Boolean(ticketsPurchased?.some(({ purchasedNumber }) => (purchasedNumber ?? 0) > 0));

export const RaffleEligibility = () => {
  const { form, formCategory, formObject, formOrganization } = useFormV2Context(FormType.Ticketing);
  const [modalOpen, setModalOpen] = useState(false);
  const [blockedRegion, setBlockedRegion] = useState<string | null>(null);
  const eligibilityError = form.formState.errors.raffleEligibility;
  const blockedAddressRef = useRef<{ country: string | undefined; region: string | undefined } | null>(null);
  const handledEligibilityErrorRef = useRef<typeof eligibilityError>(undefined);
  const elements = useElements();
  const { logAmplitudeEvent } = useAmplitude();
  const country = form.watch("country");
  const region = form.watch("region");
  const hasEntries = hasRaffleEntries(form.watch("ticketsPurchased"));
  const { currentCountryRegions } = useCountryRegion(country);
  const isRaffle = formCategory === TicketingFormCategory.Lottery;
  const isStateBanEnforced = isRaffle && isRaffleBuyerStateBanEnforced(formObject.createdAtUtc);
  const isBanned =
    isStateBanEnforced && isRaffleStateBanned(convertCountryNameToCountryCode(country as CountryNames), region);

  const validateRaffleEligibility = useCallback(async () => {
    if (!isStateBanEnforced || !hasRaffleEntries(form.getValues("ticketsPurchased"))) {
      return true;
    }
    const addressElement = elements?.getElement(AddressElement);
    const billingAddress = addressElement ? (await addressElement.getValue()).value.address : undefined;
    const currentCountry = billingAddress?.country ?? form.getValues("country");
    const currentRegion = billingAddress?.state ?? form.getValues("region");
    const countryCode = billingAddress
      ? billingAddress.country === CountryCode.US
        ? CountryCode.US
        : undefined
      : convertCountryNameToCountryCode(currentCountry as CountryNames);
    const isCurrentAddressBanned = isRaffleStateBanned(countryCode, currentRegion);

    if (isCurrentAddressBanned) {
      blockedAddressRef.current = { country: currentCountry, region: currentRegion };
    }
    return !isCurrentAddressBanned;
  }, [elements, form, isStateBanEnforced]);

  useController({ control: form.control, name: "raffleEligibility", rules: { validate: validateRaffleEligibility } });

  useEffect(() => {
    const blockedAddress = blockedAddressRef.current;
    if (!eligibilityError || !blockedAddress || handledEligibilityErrorRef.current === eligibilityError) {
      return;
    }
    handledEligibilityErrorRef.current = eligibilityError;
    setModalOpen(true);
    setBlockedRegion(
      getRegionDisplayName(OrganizationCountry.UnitedStates, blockedAddress.region) || blockedAddress.region || null
    );
    logAmplitudeEvent(AmplitudeEvents.RaffleStateBlockerDisplayed, {
      country: blockedAddress.country,
      region: blockedAddress.region,
      ticketingId: formObject.id,
      is_sweepstakes: false,
    });
  }, [eligibilityError, formObject.id, logAmplitudeEvent]);

  useEffect(() => {
    if ((!isBanned || !hasEntries) && form.formState.errors.raffleEligibility) {
      form.clearErrors("raffleEligibility");
    }
  }, [form, hasEntries, isBanned]);

  const regionName = useMemo(
    () => currentCountryRegions.find((item) => item.shortCode === region)?.name || region || "your state",
    [currentCountryRegions, region]
  );

  if (!isRaffle) {
    return null;
  }

  return (
    <>
      {hasEntries && <RaffleAdultConfirmation control={form.control} error={form.formState.errors.isAdultConfirmed} />}
      <RaffleEligibilityContent
        organizationName={formOrganization.name}
        regionName={blockedRegion || regionName}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
};
